/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { createReducer, on } from '@ngrx/store';
import moment  from 'moment';

import {
  ClientDetailActions,
  ClientDetailsDashboardActions,
  FeatureAccessibilityActions,
  InvoicesActions,
  BrandingActions,
} from '../actions';
import { Client, ClientPlan, ContactInfo } from '@truckstop-tms/zuum-lib';
export const CLIENT_DETAIL_FEATURE_KEY = 'clientDetails';

export interface ClientDetailsState {
  client: Client;
  branchesAndAgencies: Client[];
  contactInfo: ContactInfo | null;
  accountingContactInfo: ContactInfo | null;
  activePlan: ClientPlan | null;
  loading: boolean;
  loaded: boolean;
  errorMessage: string;
  isUpdating: boolean;
}

export const initialState: ClientDetailsState = {
  client: null as any,
  branchesAndAgencies: [],
  contactInfo: null,
  accountingContactInfo: null,
  activePlan: null,
  loading: false,
  loaded: false,
  isUpdating: false,
  errorMessage: '',
};

export const clientDetailsReducer = createReducer(
  initialState,

  on(ClientDetailActions.load, ClientDetailsDashboardActions.deleteClient, (state) => ({
    ...state,
    loading: true,
  })),

  on(ClientDetailActions.reload, (state) => ({
    ...state,
    errorMessage: '',
  })),

  on(ClientDetailActions.loadSuccess, ClientDetailActions.setClientDetails, (state, { client }) => ({
    ...state,
    client,
    activePlan: client.plans.find(
      (plan) => moment(plan.startDate).isSameOrBefore() && (!plan.endDate || moment(plan.endDate).isSameOrAfter())
    ) as ClientPlan,
    loading: false,
    loaded: true,
  })),

  on(ClientDetailActions.loadBranchesAndAgenciesSuccess, (state, { clients }) => ({
    ...state,
    branchesAndAgencies: clients,
  })),

  on(ClientDetailActions.loadBranchesAndAgenciesFailure, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
  })),

  on(
    ClientDetailActions.loadFailure,
    ClientDetailActions.updateClientDetailsFailure,
    ClientDetailsDashboardActions.deleteClientFailure,
    (state, { errorMessage }) => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(ClientDetailActions.reset, (state) => ({
    ...state,
    ...initialState,
  })),

  on(ClientDetailActions.updateClientDetails, InvoicesActions.simulateInvoiceGeneration, (state) => ({
    ...state,
    isUpdating: true,
  })),

  on(InvoicesActions.simulateInvoiceGenerationSuccess, (state) => ({
    ...state,
    isUpdating: false,
  })),

  on(ClientDetailActions.updateClientDetailsSuccess, (state, { client }) => ({
    ...state,
    client: { ...state.client, ...client },
    isUpdating: false,
  })),

  on(ClientDetailActions.updateClientMaxNumberOfUsersSuccess, (state, { activePlan }) => ({
    ...state,
    activePlan: { ...state.activePlan, ...activePlan },
    isUpdating: false,
  })),

  on(BrandingActions.createUpdateBrandingSuccess, (state, { branding }) => ({
    ...state,
    client: { ...state.client, branding },
  })),

  on(
    ClientDetailActions.updateMongoCompanySuccess,
    ClientDetailActions.deleteCompanyDocumentSuccess,
    (state, { company }) => ({
      ...state,
      client: { ...state.client, esData: { ...state.client?.esData, _source: { ...company } } },
    })
  ),

  on(ClientDetailActions.updateClientStatusSuccess, (state, { client }) => ({
    ...state,
    client: state.client
      ? { ...state.client, status: state.client.id === client.id ? client.status : state.client.status }
      : state.client,
  })),

  on(ClientDetailActions.cancelClientPlan, (state) => ({
    ...state,
    isUpdating: true,
  })),

  on(ClientDetailActions.cancelClientPlanSuccess, (state, { client }) => ({
    ...state,
    client: { ...state.client, ...client },
    isUpdating: false,
  })),

  on(
    ClientDetailActions.cancelClientPlanFailure,
    InvoicesActions.simulateInvoiceGenerationFailure,
    (state, { errorMessage }) => ({
      ...state,
      errorMessage,
      isUpdating: false,
    })
  ),

  on(ClientDetailActions.invitationSentSuccess, (state, { client }) => ({
    ...state,
    client: { ...state.client, invitationSentAt: client.invitationSentAt },
  })),

  on(ClientDetailActions.invitationSentFailure, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
  })),

  on(FeatureAccessibilityActions.updateSuccess, (state, { company }) => ({
    ...state,
    client: { ...state.client, esData: { ...state.client.esData, _source: company } },
  }))
);
